import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  styled,
  Autocomplete,
  Tooltip,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CollapsibleSection from "./CollapsibleSection";
import { useCheckoutSimplifiedUI } from "../../CheckoutSimplifiedUIProvider";
import type { Address } from "../../types";
import {
  autocompleteAddress,
  type CheckoutSessionAutocompleteResponse,
} from "../../../../../../api/rest/checkoutSimplifiedApi";

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "0",
  paddingBottom: "8px",
});

const FormRow = styled(Box)({
  display: "flex",
  gap: "16px",
  "@media (max-width: 768px)": {
    flexDirection: "column",
  },
});

// US States for dropdown
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

interface ShippingAddressSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  onComplete: () => void;
  disabled?: boolean;
}

export const ShippingAddressSection = ({
  isOpen,
  onToggle,
  onComplete,
  disabled = false,
}: ShippingAddressSectionProps) => {
  const { session, updateAddress, updatingAddress } = useCheckoutSimplifiedUI();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [formData, setFormData] = useState<Address>({
    name: "",
    line_one: "",
    line_two: "",
    city: "",
    state: "",
    country: "US",
    postal_code: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof Address, string>>>(
    {}
  );
  const [phoneNumberError, setPhoneNumberError] =
    useState<string | undefined>();
  const [touched, setTouched] = useState<
    Partial<Record<keyof Address, boolean>>
  >({});
  const [phoneNumberTouched, setPhoneNumberTouched] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // Refs to track when resetting via Escape key
  const isResettingNameRef = useRef(false);
  const isResettingPhoneRef = useRef(false);
  const isResettingFieldRef = useRef<Record<keyof Address, boolean>>({
    name: false,
    line_one: false,
    line_two: false,
    city: false,
    state: false,
    country: false,
    postal_code: false,
  });
  const successTimeoutRef = useRef<NodeJS.Timeout>();
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout>();

  // Address autocomplete state
  type AddressOption = NonNullable<
    CheckoutSessionAutocompleteResponse["autocompleteResult"]
  >[number];
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [addressInputValue, setAddressInputValue] = useState("");
  const [loadingAddressOptions, setLoadingAddressOptions] = useState(false);
  const addressFetchTimeoutRef = useRef<NodeJS.Timeout>();
  const addressInputRef = useRef<HTMLInputElement>(null);

  // Initialize form from session address
  useEffect(() => {
    if (session?.fulfillment_address) {
      setFormData(session.fulfillment_address);
      setAddressInputValue(session.fulfillment_address.line_one);

      // Split name into first and last name
      const nameParts = session.fulfillment_address.name.split(" ");
      if (nameParts.length > 1) {
        setFirstName(nameParts[0]);
        setLastName(nameParts.slice(1).join(" "));
      } else {
        setFirstName(session.fulfillment_address.name);
        setLastName("");
      }
    }

    // Initialize phone from buyer info if available
    if (session?.buyer?.phone_number) {
      setPhoneNumber(session.buyer.phone_number);
    }
  }, [session?.fulfillment_address, session?.buyer]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
      }
    };
  }, []);

  // Fetch address autocomplete suggestions
  const fetchAddressSuggestions = useCallback(
    async (searchText: string) => {
      if (!searchText || !session?.id) {
        setAddressOptions([]);
        return;
      }

      try {
        setLoadingAddressOptions(true);
        const response = await autocompleteAddress(session.id, {
          searchText,
          countryCodes: ["US"],
        });

        if (response.status === "success" && response.autocompleteResult) {
          setAddressOptions(response.autocompleteResult);
        } else {
          setAddressOptions([]);
        }
      } catch (error) {
        console.error("Failed to fetch address suggestions:", error);
        setAddressOptions([]);
      } finally {
        setLoadingAddressOptions(false);
      }
    },
    [session?.id]
  );

  // Parse address from terms and auto-fill fields
  const handleSelectAddress = useCallback(
    (option: AddressOption) => {
      // Parse address from terms array
      // Expected structure: [street_number, street_name, city, state, country]
      const terms = option.terms;
      
      if (!terms || terms.length < 3) {
        // Not enough information, just use main_text
        const address = option.structured_formatting.main_text;
        setAddressInputValue(address);
        setFormData((prev) => ({ ...prev, line_one: address }));
        return;
      }

      // Extract address components from terms
      let streetAddress = "";
      let city = "";
      let state = "";

      // Street address is typically the first 2 terms (number + street name)
      if (terms.length >= 2) {
        streetAddress = `${terms[0].value} ${terms[1].value}`.trim();
      } else if (terms.length >= 1) {
        streetAddress = terms[0].value;
      }

      // City is typically the 3rd term
      if (terms.length >= 3) {
        city = terms[2].value;
      }

      // State is typically the 4th term
      if (terms.length >= 4) {
        state = terms[3].value;
      }

      // Validate the street address
      const streetError = validate("line_one", streetAddress);
      
      // Update form data
      setAddressInputValue(streetAddress);
      setFormData((prev) => ({
        ...prev,
        line_one: streetAddress,
        city: city,
        state: state,
      }));

      // Clear any errors for auto-filled fields
      setErrors((prev) => ({
        ...prev,
        line_one: streetError,
        city: undefined,
        state: undefined,
      }));

      // Blur the street address field if there's no error
      if (!streetError) {
        setTimeout(() => {
          addressInputRef.current?.blur();
        }, 100);
      }
    },
    []
  );

  // Debounced address autocomplete
  useEffect(() => {
    if (addressFetchTimeoutRef.current) {
      clearTimeout(addressFetchTimeoutRef.current);
    }

    if (addressInputValue.length >= 3) {
      addressFetchTimeoutRef.current = setTimeout(() => {
        fetchAddressSuggestions(addressInputValue);
      }, 300);
    } else {
      setAddressOptions([]);
    }

    return () => {
      if (addressFetchTimeoutRef.current) {
        clearTimeout(addressFetchTimeoutRef.current);
      }
    };
  }, [addressInputValue, fetchAddressSuggestions]);

  const validate = (
    field: keyof Address,
    value: string
  ): string | undefined => {
    switch (field) {
      case "name":
        return value.trim() ? undefined : "Name is required";
      case "line_one":
        return value.trim() ? undefined : "Street address is required";
      case "city":
        return value.trim() ? undefined : "City is required";
      case "state":
        return value.trim() ? undefined : "State is required";
      case "postal_code":
        if (!value.trim()) return "Postal code is required";
        if (!/^\d{5}(-\d{4})?$/.test(value)) return "Invalid ZIP code";
        return undefined;
      default:
        return undefined;
    }
  };

  const validatePhoneNumber = (value: string): string | undefined => {
    if (!value.trim()) return "Phone number is required";
    // Basic phone number validation - allows various formats
    const phoneRegex = /^[\d\s\-()+ ]+$/;
    if (!phoneRegex.test(value)) return "Invalid phone number";
    // Check if there are at least 10 digits
    const digits = value.replace(/\D/g, "");
    if (digits.length < 10) return "Phone number must have at least 10 digits";
    return undefined;
  };

  const handleChange =
    (field: keyof Address) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));

      if (touched[field]) {
        const error = validate(field, value);
        setErrors((prev) => ({ ...prev, [field]: error }));
      }
    };

  // Track if we've already saved this address to avoid duplicate calls
  const lastSavedAddressRef = useRef<string>("");

  const hasAddressChanged = useCallback((): boolean => {
    if (!session?.fulfillment_address) return true;

    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const currentAddress = session.fulfillment_address;

    return (
      fullName !== currentAddress.name ||
      formData.line_one !== currentAddress.line_one ||
      formData.line_two !== (currentAddress.line_two || "") ||
      formData.city !== currentAddress.city ||
      formData.state !== currentAddress.state ||
      formData.postal_code !== currentAddress.postal_code
    );
  }, [session?.fulfillment_address, firstName, lastName, formData]);

  const isFormValid = useCallback((): boolean => {
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    return !!(
      fullName &&
      formData.line_one &&
      formData.city &&
      formData.state &&
      formData.postal_code &&
      phoneNumber &&
      !errors.name &&
      !errors.line_one &&
      !errors.city &&
      !errors.state &&
      !errors.postal_code &&
      !phoneNumberError
    );
  }, [firstName, lastName, formData, phoneNumber, errors, phoneNumberError]);

  const handleBlur = (field: keyof Address) => () => {
    // Skip validation if we're resetting via Escape
    if (isResettingFieldRef.current[field]) {
      isResettingFieldRef.current[field] = false;
      return;
    }

    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validate(field, formData[field] || "");
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleNameBlur = () => {
    // Skip validation if we're resetting via Escape
    if (isResettingNameRef.current) {
      isResettingNameRef.current = false;
      return;
    }

    setTouched((prev) => ({ ...prev, name: true }));
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const error = validate("name", fullName);
    setErrors((prev) => ({ ...prev, name: error }));
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      // Revert to original session values
      if (session?.fulfillment_address?.name) {
        const nameParts = session.fulfillment_address.name.split(" ");
        if (nameParts.length > 1) {
          setFirstName(nameParts[0]);
          setLastName(nameParts.slice(1).join(" "));
        } else {
          setFirstName(session.fulfillment_address.name);
          setLastName("");
        }
      } else {
        setFirstName("");
        setLastName("");
      }
      setErrors((prev) => ({ ...prev, name: undefined }));
      setTouched((prev) => ({ ...prev, name: false }));
      
      // Set flag to skip blur validation
      isResettingNameRef.current = true;
      
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleFieldKeyDown =
    (field: keyof Address) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        // Revert to original session value
        const originalValue = session?.fulfillment_address?.[field] || "";
        setFormData((prev) => ({ ...prev, [field]: originalValue }));
        
        // For line_one, also update the address input value
        if (field === "line_one") {
          setAddressInputValue(originalValue);
        }
        
        setErrors((prev) => ({ ...prev, [field]: undefined }));
        setTouched((prev) => ({ ...prev, [field]: false }));
        
        // Set flag to skip blur validation
        isResettingFieldRef.current[field] = true;
        
        (e.target as HTMLInputElement).blur();
      }
    };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      // Revert to original session value
      const originalPhone = session?.buyer?.phone_number || "";
      setPhoneNumber(originalPhone);
      setPhoneNumberError(undefined);
      setPhoneNumberTouched(false);
      
      // Set flag to skip blur validation
      isResettingPhoneRef.current = true;
      
      (e.target as HTMLInputElement).blur();
    }
  };

  // Use effect to auto-save when form becomes valid and has changed
  useEffect(() => {
    const saveAddress = async () => {
      if (!isFormValid() || !hasAddressChanged()) {
        return;
      }

      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const addressToSave = { ...formData, name: fullName };
      const addressKey = JSON.stringify(addressToSave);

      // Don't save if we just saved this exact address
      if (addressKey === lastSavedAddressRef.current) {
        return;
      }

      try {
        await updateAddress(addressToSave);
        lastSavedAddressRef.current = addressKey;
        onComplete();
        
        // Show success checkmark
        setShowSuccess(true);
        setIsFadingOut(false);
        
        // Clear any existing timeouts
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        if (fadeOutTimeoutRef.current) {
          clearTimeout(fadeOutTimeoutRef.current);
        }
        
        // Start fade out after 2 seconds
        successTimeoutRef.current = setTimeout(() => {
          setIsFadingOut(true);
          
          // Remove from DOM after fade completes
          fadeOutTimeoutRef.current = setTimeout(() => {
            setShowSuccess(false);
            setIsFadingOut(false);
          }, 500); // Match the transition duration
        }, 2000);
      } catch (err) {
        console.error("Failed to update address:", err);
      }
    };

    // Debounce the save to avoid rapid calls
    const timeoutId = setTimeout(saveAddress, 800);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, formData, phoneNumber]);

  return (
    <CollapsibleSection
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={disabled}
      title="Shipping address"
      titleIcon={
        updatingAddress ? (
          <CircularProgress
            size={16}
            thickness={4}
            sx={{
              color: "text.secondary",
              opacity: 0.6,
            }}
          />
        ) : showSuccess ? (
          <CheckCircleIcon
            sx={{
              fontSize: 20,
              color: "success.main",
              opacity: isFadingOut ? 0 : 1,
              transform: isFadingOut ? "scale(0.8)" : "scale(1)",
              transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
              animation: isFadingOut ? "none" : "fadeIn 0.3s ease-in",
              "@keyframes fadeIn": {
                from: { opacity: 0, transform: "scale(0.8)" },
                to: { opacity: 1, transform: "scale(1)" },
              },
            }}
          />
        ) : null
      }
      hideControl={false}
    >
      <FormContainer>
        <FormRow>
          <TextField
            fullWidth
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            error={touched.name && !!errors.name}
            helperText={touched.name && errors.name}
            disabled={disabled}
            required
          />
          <TextField
            fullWidth
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            error={touched.name && !!errors.name}
            disabled={disabled}
            required
          />
        </FormRow>
        <Autocomplete
          fullWidth
          freeSolo
          options={addressOptions}
          loading={loadingAddressOptions}
          inputValue={addressInputValue}
          onInputChange={(_, newValue, reason) => {
            // Only update local input state while typing, don't update formData
            if (reason === "input") {
              setAddressInputValue(newValue);
              // Validate in real-time but don't save
              if (touched.line_one) {
                const error = validate("line_one", newValue);
                setErrors((prev) => ({ ...prev, line_one: error }));
              }
            }
          }}
          onChange={(_, newValue) => {
            // Only update formData when user selects from dropdown
            if (typeof newValue === "string") {
              setAddressInputValue(newValue);
              setFormData((prev) => ({ ...prev, line_one: newValue }));
            } else if (newValue) {
              // User selected from dropdown - parse and auto-fill
              handleSelectAddress(newValue);
            }
          }}
          onBlur={() => {
            // Update formData on blur
            setFormData((prev) => ({ ...prev, line_one: addressInputValue }));
            handleBlur("line_one")();
          }}
          disabled={disabled}
          filterOptions={(x) => x}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            // Return only the main text, not the full description
            return option.structured_formatting.main_text;
          }}
          isOptionEqualToValue={(option, value) => {
            if (typeof option === "string" && typeof value === "string") {
              return option === value;
            }
            if (typeof option === "object" && typeof value === "object") {
              return option.place_id === value.place_id;
            }
            return false;
          }}
          renderOption={(props, option) => {
            // Use place_id as unique key instead of the default behavior
            const { key, ...otherProps } = props;
            return (
              <li key={option.place_id} {...otherProps}>
                <Box>
                  <Box sx={{ fontWeight: 500 }}>
                    {option.structured_formatting.main_text}
                  </Box>
                  <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                    {option.structured_formatting.secondary_text}
                  </Box>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Street address"
              error={touched.line_one && !!errors.line_one}
              helperText={touched.line_one && errors.line_one}
              required
              inputRef={addressInputRef}
              onKeyDown={handleFieldKeyDown("line_one")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingAddressOptions ? (
                      <CircularProgress size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <TextField
          fullWidth
          label="Apartment, suite, etc. (optional)"
          value={formData.line_two}
          onChange={handleChange("line_two")}
          onBlur={handleBlur("line_two")}
          onKeyDown={handleFieldKeyDown("line_two")}
          disabled={disabled}
        />
        <FormRow>
          <TextField
            fullWidth
            label="City"
            value={formData.city}
            onChange={handleChange("city")}
            onBlur={handleBlur("city")}
            onKeyDown={handleFieldKeyDown("city")}
            error={touched.city && !!errors.city}
            helperText={touched.city && errors.city}
            disabled={disabled}
            required
          />
          <Autocomplete
            fullWidth
            options={US_STATES}
            getOptionLabel={(option) => option.name}
            value={US_STATES.find((s) => s.code === formData.state) || null}
            onChange={(_, newValue) => {
              const value = newValue?.code || "";
              setFormData((prev) => ({ ...prev, state: value }));
              if (touched.state) {
                const error = validate("state", value);
                setErrors((prev) => ({ ...prev, state: error }));
              }
            }}
            onBlur={handleBlur("state")}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                error={touched.state && !!errors.state}
                helperText={touched.state && errors.state}
                required
                onKeyDown={handleFieldKeyDown("state")}
              />
            )}
          />
        </FormRow>
        <FormRow>
          <TextField
            fullWidth
            label="ZIP code"
            value={formData.postal_code}
            onChange={handleChange("postal_code")}
            onBlur={handleBlur("postal_code")}
            onKeyDown={handleFieldKeyDown("postal_code")}
            error={touched.postal_code && !!errors.postal_code}
            helperText={touched.postal_code && errors.postal_code}
            disabled={disabled}
            required
          />
          <TextField fullWidth label="Country" value="United States" disabled />
        </FormRow>
        <TextField
          fullWidth
          type="tel"
          label="Phone"
          value={phoneNumber}
          onChange={(e) => {
            const value = e.target.value;
            setPhoneNumber(value);
            if (phoneNumberTouched) {
              setPhoneNumberError(validatePhoneNumber(value));
            }
          }}
          onBlur={() => {
            // Skip validation if we're resetting via Escape
            if (isResettingPhoneRef.current) {
              isResettingPhoneRef.current = false;
              return;
            }
            setPhoneNumberTouched(true);
            setPhoneNumberError(validatePhoneNumber(phoneNumber));
          }}
          onKeyDown={handlePhoneKeyDown}
          error={phoneNumberTouched && !!phoneNumberError}
          helperText={phoneNumberTouched && phoneNumberError}
          disabled={disabled}
          placeholder="+1 234-567-8900"
          required
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title="In case we need to contact you about your order"
                  arrow
                  placement="top"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        fontSize: "14px",
                        padding: "12px 16px",
                        maxWidth: "300px",
                      },
                    },
                  }}
                >
                  <IconButton edge="end" size="small" sx={{ padding: "8px" }}>
                    <HelpOutlineIcon fontSize="small" sx={{ color: "#666" }} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </FormContainer>
    </CollapsibleSection>
  );
};
